import { useEffect, useState } from "react";
import { userPhotoUrl } from "./api";

/**
 * A user's profile photo, fetched with auth as an object URL (the token is in
 * memory, so a plain <img src="/api/…"> would 401). Falls back to initials on a
 * coloured disc when there is no photo. `refreshKey` forces a re-fetch after an
 * upload replaces the image.
 */
export function UserAvatar({
  userId,
  hasPhoto,
  name,
  size = 40,
  refreshKey = 0,
}: {
  userId: string;
  hasPhoto: boolean;
  name: string;
  size?: number;
  refreshKey?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPhoto) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    userPhotoUrl(userId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, hasPhoto, refreshKey]);

  const initials =
    name
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const style = { width: size, height: size } as const;

  if (url) {
    return (
      <img
        src={url}
        alt={name}
        style={style}
        className="shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div
      style={style}
      aria-label={name}
      className="flex shrink-0 items-center justify-center rounded-full bg-primary/15 font-medium text-primary"
    >
      <span style={{ fontSize: Math.max(11, size / 2.6) }}>{initials}</span>
    </div>
  );
}
